import { useCallback, useEffect, useMemo } from "react";
import { IndexFiltersMode } from "@shopify/polaris";
import { getCurrencySymbol, softAssertNumber } from "../helper_functions/utils";
import {
  allDiscounted,
  allTypes,
  useHorseVariantsProductTypeOptions,
  useHorseVariantTagsOptions,
  useHorseVariantVendorsOptions,
  useShopifyChannelAvailabilityOptions,
  useSuppliersSearchOptions,
  useUser,
} from "../api_utils/requests";
import { useSetIndexFiltersMode } from "@shopify/polaris";
import type {
  ArchivedChoice,
  BaseQueryParams,
  SalesTrendsQueryParams,
} from "../api_utils/types";
import { useStringHandler } from "./useHandlers";
import { useIntSelect, useMoreThanLessThanField, useStringMultiSelect, useStringSelect } from "./useFields";

const stockInfoOptions = [
  { label: "All", value: "all", key: "all" },
  { label: "Never available", value: "never_available", key: "never_available" },
  { label: "No sales", value: "no_sales", key: "no_sales" },
  { label: "Out of stock", value: "out_of_stock", key: "out_of_stock" },
  { label: "Out of stock quite soon (<30 days)", value: "out_of_stock_quite_soon", key: "out_of_stock_quite_soon" },
  { label: "Out of stock soon (<60 days)", value: "out_of_stock_soon_ish", key: "out_of_stock_soon_ish" },
  { label: "Overstock", value: "overstock", key: "overstock" },
  { label: "Below minimum", value: "below_min", key: "below_min" },
];

const archiveStatusOptions = [
  { label: "Hide archived", value: "undiscarded", key: "undiscarded" },
  { label: "Show all", value: "all", key: "all" },
];

const discountedOptions = [
  { label: "All", value: allDiscounted, key: "all" },
  { label: "Only discounted", value: "true", key: "true" },
  { label: "Only non-discounted", value: "false", key: "false" },
]

export const useSalesTrendsFilters = ({
  setFilters,
}: {
  readonly setFilters: (filters: Partial<SalesTrendsQueryParams>) => void;
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
}) => {
  // Parse URL params
  const searchParams = useMemo(() => new URLSearchParams(document.location.search), []);

  const {
    queryParam: unarchivedHorseVariantsQueryParam,
    value: horseVariantArchiveStatus,
    handleRemove: handleArchiveStatusRemove,
    setValue: setArchiveStatus,
    appliedFilter: unarchivedHorseVariantsAppliedFilter,
    filterComponent: archivedStatusFilterComponent,
  } = useStringSelect<ArchivedChoice>({
    searchParams,
    key: "unarchived_horse_variants",
    options: archiveStatusOptions,
    allValue: "all",
    filterLabel: "Archived status",
    defaultValue: "undiscarded",
    onlyShowNonDefault: true,
  });

  const { data: productTypes } = useHorseVariantsProductTypeOptions({
    collection: horseVariantArchiveStatus,
  });
  const { data: vendors } = useHorseVariantVendorsOptions({
    collection: horseVariantArchiveStatus,
  });
  const { data: shopifyChannelOptions } = useShopifyChannelAvailabilityOptions();
  const { data: suppliers } = useSuppliersSearchOptions();
  const { data: productTagsOptions } = useHorseVariantTagsOptions();
  const { data: { user: { currency: userCurrency } } } = useUser();

  const {
    value: search,
    setValue: setSearch,
    handleRemove: handleSearchRemove,
  } = useStringHandler(searchParams, "search");
  const {
    queryParam: supplierQueryParam,
    value: supplierId,
    handleRemove: handleSupplierIdRemove,
    setValue: setSupplierId,
    appliedFilter: supplierAppliedFilter,
    filterComponent: supplierFilterComponent,
  } = useIntSelect({
    searchParams,
    labelPrefix: "Supplier",
    key: "supplier_id",
    options: suppliers,
  });
  const {
    queryParam: vendorQueryParam,
    value: vendor,
    handleRemove: handleVendorRemove,
    setValue: setVendor,
    appliedFilter: vendorAppliedFilter,
    filterComponent: vendorFilterComponent,
  } = useStringSelect({
    searchParams,
    labelPrefix: "Vendor",
    key: "vendor",
    options: vendors,
  });
  const {
    queryParam: productTypeQueryParam,
    value: productType,
    handleRemove: handleProductTypeRemove,
    setValue: setProductType,
    appliedFilter: productTypeAppliedFilter,
    filterComponent: productTypeFilterComponent,
  } = useStringSelect({
    searchParams,
    labelPrefix: "Product type",
    key: "product_type",
    options: productTypes,
    allValue: allTypes,
  });
  const {
    queryParam: discountedQueryParam,
    value: discounted,
    handleRemove: handleDiscountedRemove,
    setValue: setDiscounted,
    appliedFilter: discountedAppliedFilter,
    filterComponent: discountedFilterComponent,
  } = useStringSelect({
    searchParams,
    key: "discounted",
    options: discountedOptions,
    allValue: allDiscounted,
    labelPrefix: "Discounted?",
    defaultValue: allDiscounted,
    onlyShowNonDefault: true,
  });
  const {
    queryParam: stockInfoQueryParam,
    value: stockInfo,
    handleRemove: handleStockInfoRemove,
    setValue: setStockInfo,
    appliedFilter: stockInfoAppliedFilter,
    filterComponent: stockInfoFilterComponent,
  } = useStringSelect({
    searchParams,
    labelPrefix: "Stock info",
    key: "info_query",
    options: stockInfoOptions,
    allValue: "all",
  });
  const published_shopify_channel_ids = searchParams.getAll("published_shopify_channel_ids[]")
  const unpublished_shopify_channel_ids = searchParams.getAll("unpublished_shopify_channel_ids[]")
  let defaultShopifyChannel: string;
  if (published_shopify_channel_ids.length > 0) {
    defaultShopifyChannel = published_shopify_channel_ids[0] + ":true";
  } else if (unpublished_shopify_channel_ids.length > 0) {
    defaultShopifyChannel = unpublished_shopify_channel_ids[0] + ":false";
  }
  const {
    value: shopifyChannel,
    handleRemove: handleShopifyChannelRemove,
    setValue: setShopifyChannel,
    appliedFilter: shopifyChannelAppliedFilter,
    filterComponent: shopifyChannelFilterComponent,
  } = useStringSelect({
    searchParams,
    key: "shopify_channel",
    options: shopifyChannelOptions,
    filterLabel: "Publishing",
    defaultValue: defaultShopifyChannel,
  });
  const {
    queryParam: productTagsQueryParam,
    values: productTags,
    setValues: setProductTags,
    handleAdd: handleNewTagAddition,
    handleRemove: handleProductTagsRemove,
    appliedFilter: productTagsAppliedFilter,
    filterComponent: productTagsFilterComponent,
  } = useStringMultiSelect({
    searchParams,
    labelPrefix: "Tags",
    key: "product_tags",
    options: productTagsOptions,
  });
  const {
    queryParam: orderedQuantityQueryParam,
    value: orderedQuantity,
    setValue: setOrderedQuantity,
    handleRemove: handleOrderedQuantityRemove,
    appliedFilter: orderedQuantityAppliedFilter,
    filterComponent: orderedQuantityFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_ordered_quantity",
    label: "Ordered quantity",
  });
  const {
    queryParam: costQueryParam,
    value: cost,
    setValue: setCost,
    handleRemove: handleCostRemove,
    appliedFilter: costAppliedFilter,
    filterComponent: costFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_cost",
    label: "Cost",
    valuePrefix: getCurrencySymbol(userCurrency),
  });
  const {
    queryParam: revenueQueryParam,
    value: revenue,
    setValue: setRevenue,
    handleRemove: handleRevenueRemove,
    appliedFilter: revenueAppliedFilter,
    filterComponent: revenueFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_revenue",
    label: "Revenue",
    valuePrefix: getCurrencySymbol(userCurrency),
  });
  const {
    queryParam: salesQuantityQueryParam,
    value: salesQuantity,
    setValue: setSalesQuantity,
    handleRemove: handleSalesQuantityRemove,
    appliedFilter: salesQuantityAppliedFilter,
    filterComponent: salesQuantityFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_sales_quantity",
    label: "Sales quantity",
  });
  const {
    queryParam: salesRateQueryParam,
    value: salesRate,
    setValue: setSalesRate,
    handleRemove: handleSalesRateRemove,
    appliedFilter: salesRateAppliedFilter,
    filterComponent: salesRateFilterComponent,
  } = useMoreThanLessThanField<number>({
    searchParams,
    key: "by_sales_rate",
    label: "Sales rate",
  });
  const {
    queryParam: weightQueryParam,
    value: weight,
    setValue: setWeight,
    handleRemove: handleWeightRemove,
    appliedFilter: weightAppliedFilter,
    filterComponent: weightFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_weight",
    label: "Weight",
  });
  const {
    queryParam: currentlyAvailableQueryParam,
    value: currentlyAvailable,
    setValue: setCurrentlyAvailable,
    handleRemove: handleCurrentlyAvailableRemove,
    appliedFilter: currentlyAvailableAppliedFilter,
    filterComponent: currentlyAvailableFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_currently_available",
    label: "Available quantity",
  });
  const {
    queryParam: availableDaysQueryParam,
    value: availableDays,
    setValue: setAvailableDays,
    handleRemove: handleAvailableDaysRemove,
    appliedFilter: availableDaysAppliedFilter,
    filterComponent: availableDaysFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_available_days",
    label: "Days available",
  });
  const {
    queryParam: daysRemainingQueryParam,
    value: daysRemaining,
    setValue: setDaysRemaining,
    handleRemove: handleDaysRemainingRemove,
    appliedFilter: daysRemainingAppliedFilter,
    filterComponent: daysRemainingFilterComponent,
  } = useMoreThanLessThanField<string>({
    searchParams,
    key: "by_days_remaining",
    label: "Days remaining",
  });

  // Memoize expensive computations
  const queryParams = useMemo<BaseQueryParams>((): BaseQueryParams => {
    const shopifyChannelOption = shopifyChannelOptions.find((channel) => channel.value === shopifyChannel);
    const published_shopify_channel_ids: number[] = [];
    const unpublished_shopify_channel_ids: number[] = [];

    if (shopifyChannelOption) {
      const [shopifyChannelIds, isPublished] = shopifyChannelOption.value.split(":");
      if (isPublished === "true") {
        published_shopify_channel_ids.push(...shopifyChannelIds.split("|").map(softAssertNumber));
      } else if (isPublished === "false") {
        unpublished_shopify_channel_ids.push(...shopifyChannelIds.split("|").map(softAssertNumber));
      }
    }

    return {
      ...availableDaysQueryParam,
      ...costQueryParam,
      ...currentlyAvailableQueryParam,
      ...daysRemainingQueryParam,
      ...orderedQuantityQueryParam,
      ...productTagsQueryParam,
      ...productTypeQueryParam,
      ...discountedQueryParam,
      ...revenueQueryParam,
      ...salesQuantityQueryParam,
      ...salesRateQueryParam,
      ...stockInfoQueryParam,
      ...supplierQueryParam,
      ...unarchivedHorseVariantsQueryParam,
      ...vendorQueryParam,
      ...weightQueryParam,

      published_shopify_channel_ids,
      unpublished_shopify_channel_ids,
      search,
    };
  }, [
    shopifyChannel,
    shopifyChannelOptions,

    search,

    availableDaysQueryParam,
    costQueryParam,
    currentlyAvailableQueryParam,
    daysRemainingQueryParam,
    orderedQuantityQueryParam,
    productTagsQueryParam,
    productTypeQueryParam,
    discountedQueryParam,
    revenueQueryParam,
    salesQuantityQueryParam,
    salesRateQueryParam,
    stockInfoQueryParam,
    supplierQueryParam,
    unarchivedHorseVariantsQueryParam,
    vendorQueryParam,
    weightQueryParam,
  ]);

  useEffect(() => {
    setFilters(queryParams);
  }, [queryParams, setFilters]);

  const handleFiltersClearAll = useCallback(() => {
    handleArchiveStatusRemove();
    handleAvailableDaysRemove();
    handleCostRemove();
    handleCurrentlyAvailableRemove();
    handleDaysRemainingRemove();
    handleOrderedQuantityRemove();
    handleProductTagsRemove();
    handleProductTypeRemove();
    handleRevenueRemove();
    handleSalesQuantityRemove();
    handleSalesRateRemove();
    handleSearchRemove();
    handleShopifyChannelRemove();
    handleStockInfoRemove();
    handleSupplierIdRemove();
    handleVendorRemove();
    handleWeightRemove();
  }, [
    handleArchiveStatusRemove,
    handleAvailableDaysRemove,
    handleCostRemove,
    handleCurrentlyAvailableRemove,
    handleDaysRemainingRemove,
    handleOrderedQuantityRemove,
    handleProductTagsRemove,
    handleProductTypeRemove,
    handleRevenueRemove,
    handleSalesQuantityRemove,
    handleSalesRateRemove,
    handleSearchRemove,
    handleShopifyChannelRemove,
    handleStockInfoRemove,
    handleSupplierIdRemove,
    handleVendorRemove,
    handleWeightRemove,
  ]);

  const saleTrendsFilters = useMemo(() => {
    return [
      unarchivedHorseVariantsAppliedFilter,
      availableDaysAppliedFilter,
      costAppliedFilter,
      currentlyAvailableAppliedFilter,
      daysRemainingAppliedFilter,
      orderedQuantityAppliedFilter,
      productTagsAppliedFilter,
      productTypeAppliedFilter,
      discountedAppliedFilter,
      revenueAppliedFilter,
      salesQuantityAppliedFilter,
      salesRateAppliedFilter,
      shopifyChannelAppliedFilter,
      stockInfoAppliedFilter,
      supplierAppliedFilter,
      vendorAppliedFilter,
      weightAppliedFilter,
    ].filter((appliedFilter) => {
      return !!appliedFilter;
    });
  }, [
    unarchivedHorseVariantsAppliedFilter,
    availableDaysAppliedFilter,
    costAppliedFilter,
    currentlyAvailableAppliedFilter,
    daysRemainingAppliedFilter,
    orderedQuantityAppliedFilter,
    productTagsAppliedFilter,
    productTypeAppliedFilter,
    discountedAppliedFilter,
    revenueAppliedFilter,
    salesQuantityAppliedFilter,
    salesRateAppliedFilter,
    shopifyChannelAppliedFilter,
    stockInfoAppliedFilter,
    supplierAppliedFilter,
    vendorAppliedFilter,
    weightAppliedFilter,
  ]);

  const { mode, setMode } = useSetIndexFiltersMode();

  useEffect(() => {
    if (saleTrendsFilters.length > 0) {
      setMode(IndexFiltersMode.Filtering); // Ensure filters appear expanded when applied
    }
  }, [saleTrendsFilters.length, setMode]);

  return {
    supplierId,
    setSupplierId,
    suppliers,
    supplierFilterComponent,

    productType,
    setProductType,
    handleProductTypeRemove,
    productTypes,
    productTypeFilterComponent,

    stockInfo,
    setStockInfo,
    handleStockInfoRemove,
    stockInfoOptions,
    stockInfoFilterComponent,

    productTags,
    setProductTags,
    handleNewTagAddition,
    handleProductTagsRemove,
    productTagsOptions,
    productTagsFilterComponent,

    shopifyChannel,
    setShopifyChannel,
    shopifyChannelOptions,
    shopifyChannelFilterComponent,

    vendor,
    setVendor,
    handleVendorRemove,
    vendors,
    vendorFilterComponent,

    horseVariantArchiveStatus,
    handleArchiveStatusRemove,
    setArchiveStatus,
    archiveStatusOptions,
    archivedStatusFilterComponent,

    discounted,
    handleDiscountedRemove,
    setDiscounted,
    discountedOptions,
    discountedFilterComponent,

    orderedQuantity,
    setOrderedQuantity,
    handleOrderedQuantityRemove,
    orderedQuantityFilterComponent,

    cost,
    setCost,
    handleCostRemove,
    costFilterComponent,

    revenue,
    setRevenue,
    handleRevenueRemove,
    revenueFilterComponent,

    salesQuantity,
    setSalesQuantity,
    handleSalesQuantityRemove,
    salesQuantityFilterComponent,

    salesRate,
    setSalesRate,
    handleSalesRateRemove,
    salesRateFilterComponent,

    weight,
    setWeight,
    handleWeightRemove,
    weightFilterComponent,

    currentlyAvailable,
    setCurrentlyAvailable,
    handleCurrentlyAvailableRemove,
    currentlyAvailableFilterComponent,

    availableDays,
    setAvailableDays,
    handleAvailableDaysRemove,
    availableDaysFilterComponent,

    daysRemaining,
    setDaysRemaining,
    handleDaysRemainingRemove,
    daysRemainingFilterComponent,

    search,
    setSearch,

    // Handlers
    handleSearchRemove,
    handleFiltersClearAll,

    // Applied filters
    saleTrendsFilters,

    // Mode management
    mode,
    setMode,
  };
};
